import type {ParsedStyleItem, Property} from '../types'
import {markPropertyToRemove, isPropertyToRemove, removePropertyToRemovePrefix} from './utils'

interface Padding {
    left: string
    right: string
    top: string
    bottom: string
}

const sides = ['padding-left', 'padding-right', 'padding-top', 'padding-bottom']
const paddingPropertiesToRemove = ['padding', ...sides.map(markPropertyToRemove)]

const paddingToProperty = (padding: Padding): Property[] => {
    const paddingProperties: Property[] = []
    Object.entries(padding).forEach(([side, value]) => {
        paddingProperties.push({
            prop: `padding-${side}`,
            cssProp: `padding-${side}`,
            value
        })
    })

    return paddingProperties
}

const getPaddingValues = (padding: Property): Padding => {
    const paddingValues = padding.value.split(' ')
    const paddingLength = paddingValues.length

    if (paddingLength === 1) {
        const [paddingValue] = paddingValues
        return {
            left: paddingValue,
            right: paddingValue,
            top: paddingValue,
            bottom: paddingValue
        }
    }

    if (paddingLength === 2) {
        const [paddingTopAndBottom, paddingLeftAndRight] = paddingValues
        return {
            left: paddingLeftAndRight,
            right: paddingLeftAndRight,
            top: paddingTopAndBottom,
            bottom: paddingTopAndBottom
        }
    }

    if (paddingLength === 3) {
        const [paddingTop, paddingLeftAndRight, paddingBottom] = paddingValues
        return {
            left: paddingLeftAndRight,
            right: paddingLeftAndRight,
            top: paddingTop,
            bottom: paddingBottom
        }
    }

    const [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingValues

    return {
        left: paddingLeft,
        right: paddingRight,
        top: paddingTop,
        bottom: paddingBottom
    }
}

const handlePaddingProperty = (property: Property, paddingSides: Padding): void => {
    if (property.cssProp === 'padding') {
        const {left, right, top, bottom} = getPaddingValues(property)
        paddingSides.left = left
        paddingSides.right = right
        paddingSides.top = top
        paddingSides.bottom = bottom
    }

    const sideToRemove = removePropertyToRemovePrefix(property.cssProp)

    if (sideToRemove && sides.includes(sideToRemove)) {
        const side = property.cssProp.split('-')[1] as keyof Padding
        paddingSides[side] = property.value

        if (!isPropertyToRemove(property.cssProp)) {
            property.cssProp = markPropertyToRemove(property.cssProp)
        }
    }
}

const getUpdatedProperties = (properties: Property[], paddingSides: Padding): Property[] => {
    return [
        ...properties.filter(property => !paddingPropertiesToRemove.includes(property.cssProp)),
        ...paddingToProperty(paddingSides)
    ]
}

const migratePadding = (styleItems: ParsedStyleItem[]): ParsedStyleItem[] => {
    return styleItems.map(styleItem => {
        const {parsedStyle} = styleItem
        const updatedParsedStyle = Object.entries(parsedStyle).reduce(
            (acc, [selector, {properties, mobileProperties}]) => {
                const paddingSides = {} as Padding
                const mobilePaddingSides = {} as Padding

                properties.forEach(property => {
                    handlePaddingProperty(property, paddingSides)
                })

                mobileProperties.forEach(property => {
                    handlePaddingProperty(property, mobilePaddingSides)
                })

                const updatedProperties = getUpdatedProperties(properties, paddingSides)
                const updatedMobileProperties = getUpdatedProperties(mobileProperties, mobilePaddingSides)

                return {
                    ...acc,
                    [selector]: {
                        ...acc[selector],
                        properties: updatedProperties,
                        mobileProperties: updatedMobileProperties
                    }
                }
            },
            parsedStyle
        )

        return {
            ...styleItem,
            parsedStyle: updatedParsedStyle
        }
    })
}

export default migratePadding
